import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, ArrowUp, ArrowDown, Upload, Building2, ArrowLeftRight, ExternalLink } from 'lucide-react';
import { REVENUE_TYPE_LABELS, effectiveRevenueType } from '../lib/revenueType.js';
import { apiPersons } from '../api/client.js';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';
import FilterBar from '../components/FilterBar.jsx';
import PersonFormModal from '../components/PersonFormModal.jsx';
import ImportCollaborateursModal from '../components/ImportCollaborateursModal.jsx';
import { useFilterStore, toFilterQuery } from '../hooks/useFilterStore.js';
import { PERSONS_SORT, useSortStore } from '../hooks/useSortStore.js';

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

/** Affiche un taux horaire stocké en TJM (CHF/jour) : ×8. */
function formatRate(rate) {
  if (rate === null || rate === undefined || rate === '') return '—';
  const n = typeof rate === 'string' ? Number(rate) : rate;
  if (Number.isNaN(n)) return '—';
  return `${(n * 8).toFixed(2)} CHF/j`;
}

/**
 * Affiche un tarif déjà exprimé en CHF/jour (TJM/CJM de référence Synosight,
 * publié par le référentiel central — PAS de conversion ×8).
 */
function formatDailyRate(rate) {
  if (rate === null || rate === undefined || rate === '') return '—';
  const n = typeof rate === 'string' ? Number(rate) : rate;
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(2)} CHF/j`;
}

const EMPLOYEE_TYPE_LABELS = {
  EMPLOYEE: 'Salarié',
  CONTRACTOR: 'Sous-traitant',
};

/** #21 synosight — picto du type de revenu (shadow P&L) dans la colonne Type. */
const REVENUE_TYPE_ICONS = {
  INTERNAL: { Icon: Building2, color: 'var(--primary)' },
  INTERCO: { Icon: ArrowLeftRight, color: 'var(--warning)' },
  EXTERNAL: { Icon: ExternalLink, color: 'var(--text)' },
};

function RevenueTypeIcon({ person }) {
  const { type, auto } = effectiveRevenueType(person);
  const { Icon, color } = REVENUE_TYPE_ICONS[type];
  return (
    <span
      title={`${REVENUE_TYPE_LABELS[type]}${auto ? ' (automatique)' : ' (posé dans la fiche)'}`}
      style={{ display: 'inline-flex', verticalAlign: 'text-bottom', marginRight: 6 }}
    >
      <Icon size={14} color={color} aria-label={REVENUE_TYPE_LABELS[type]} />
    </span>
  );
}

/**
 * Convention RH française (cohérent `ScheduleView.displayPersonName`) : le nom
 * de famille s'affiche TOUJOURS en MAJUSCULES, le prénom en CamelCase (première
 * lettre de chaque composant en capitale — gère les traits d'union et espaces,
 * ex. "jean-pierre" → "Jean-Pierre"). La BDD conserve la casse Float d'origine
 * (tantôt "Bondaz", tantôt "BOYER") — la normalisation est appliquée au rendu.
 */
function toCamelCaseName(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .replace(/(^|[\s-'])([a-zà-ÿ])/g, (_, sep, ch) => sep + ch.toUpperCase());
}

function displayLastName(person) {
  return (person.lastName ?? '').toUpperCase();
}

function displayFirstName(person) {
  return toCamelCaseName(person.firstName ?? '');
}

/**
 * Une personne est "sortie" quand sa date de sortie (`endDate`) est <= à la
 * date du jour (comparaison à la journée, sans heure). Elle reste affichée
 * dans la liste, mais porte un tag indicatif.
 */
function isDeparted(person, todayIso) {
  if (!person.endDate) return false;
  return String(person.endDate).slice(0, 10) <= todayIso;
}

// Tri sur la colonne "Nom complet". 4 états cycliques (cohérent ScheduleView) :
//   { lastName, asc } → { lastName, desc } → { firstName, asc } → { firstName, desc } → cycle
// Ticket #37 — l'état vit dans `useSortStore` (partagé + persisté) pour qu'une
// vue sauvegardée puisse le mémoriser et le réappliquer.

export default function PersonsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');
  const { conditions: filterConditions } = useFilterStore('persons');

  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Sprint 14 — l'éditeur est extrait dans `PersonFormModal`. On ne garde ici
  // que l'id édité (null ⇒ création).
  const [editingId, setEditingId] = useState(null);

  const { sort, setSort } = useSortStore(PERSONS_SORT);
  const cycleSort = useCallback(() => {
    setSort((s) => {
      if (s.field === 'lastName' && s.dir === 'asc') return { field: 'lastName', dir: 'desc' };
      if (s.field === 'lastName' && s.dir === 'desc') return { field: 'firstName', dir: 'asc' };
      if (s.field === 'firstName' && s.dir === 'asc') return { field: 'firstName', dir: 'desc' };
      return { field: 'lastName', dir: 'asc' };
    });
  }, []);

  const todayIso = useMemo(() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }, []);

  // Tri front, locale `fr` (gère diacritiques), sensitivity=base (insensible
  // casse/accents). Champ secondaire = l'autre champ, pour départager les
  // homonymes.
  const sortedItems = useMemo(() => {
    const opts = { sensitivity: 'base' };
    return [...items].sort((a, b) => {
      const keyA = sort.field === 'lastName'
        ? `${a.lastName} ${a.firstName}`
        : `${a.firstName} ${a.lastName}`;
      const keyB = sort.field === 'lastName'
        ? `${b.lastName} ${b.firstName}`
        : `${b.firstName} ${b.lastName}`;
      const cmp = keyA.localeCompare(keyB, 'fr', opts);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [items, sort]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiPersons.list(
        archivedFilter,
        toFilterQuery(filterConditions),
      );
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter, filterConditions]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setModalOpen(true);
  };

  const onArchive = async (p) => {
    if (!window.confirm(`Archiver ${p.firstName} ${p.lastName} ?`)) return;
    await apiPersons.archive(p.id);
    await reload();
  };

  const onRestore = async (p) => {
    await apiPersons.restore(p.id);
    await reload();
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          // `<main>` (App.jsx) est la zone de scroll et commence déjà SOUS le
          // header applicatif de 64px : l'offset sticky doit donc être 0.
          // (Un `top: 64` hérité d'un ancien layout décalait la barre vers le
          // bas et masquait la première ligne du tableau au scroll.)
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          // Ticket #34 — sur écran étroit, les filtres passent à la ligne au
          // lieu d'écraser les boutons d'action.
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Personnes</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <FilterBar scope="persons" />
        </div>
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          style={{ ...secondaryBtn, marginLeft: 'auto', flexShrink: 0 }}
          title="Importer des collaborateurs depuis un export GESCOM 430"
        >
          <Upload size={14} /> Importer (GESCOM)
        </button>
        <button type="button" onClick={openCreate} style={{ ...primaryBtn, flexShrink: 0 }}>
          <Plus size={14} /> Nouvelle personne
        </button>
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'auto',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>
                <button
                  type="button"
                  onClick={cycleSort}
                  title="Trier (Nom ↑ → Nom ↓ → Prénom ↑ → Prénom ↓)"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: 'inherit',
                    font: 'inherit',
                    fontWeight: 500,
                  }}
                >
                  {sort.field === 'lastName' ? 'Nom' : 'Prénom'} complet
                  {sort.dir === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
                </button>
              </th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Email</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Lié à</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Département</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, whiteSpace: 'nowrap' }}>Pays / Région</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Rôle</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Type</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>TJM (réf. Synosight)</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 20, color: 'var(--text-muted)' }}>Aucune personne.</td>
              </tr>
            ) : (
              sortedItems.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                    <strong style={{ fontWeight: 600 }}>{displayLastName(p)}</strong>{' '}
                    {displayFirstName(p)}
                    {isDeparted(p, todayIso) ? (
                      <span
                        title={`Sorti depuis le ${String(p.endDate).slice(0, 10)}`}
                        style={{
                          marginLeft: 8,
                          fontSize: 11,
                          fontWeight: 500,
                          color: 'var(--warning)',
                          border: '1px solid var(--warning)',
                          borderRadius: 'var(--radius-pill)',
                          padding: '1px 8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Sorti
                      </span>
                    ) : null}
                    {p.archivedAt ? (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(archivée)</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{p.email ?? '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {p.user ? p.user.email : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{p.department?.name ?? '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {p.countryCode ?? '—'} / {p.region?.name ?? '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{p.role?.name ?? '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    <RevenueTypeIcon person={p} />
                    {EMPLOYEE_TYPE_LABELS[p.employeeType] ?? p.employeeType ?? '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {p.referenceRate?.referenceTjm != null ? (
                      <span style={{ color: 'var(--text)' }} title="Tarif de référence publié par Synosight">
                        {formatDailyRate(p.referenceRate.referenceTjm)}
                      </span>
                    ) : (
                      <span title="Aucun tarif de référence Synosight (non publié au référentiel central)">—</span>
                    )}
                    {p.billRate ? (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }} title="Override local synolog (billRate)">
                        override : {formatRate(p.billRate)}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <EntityActions
                      archived={Boolean(p.archivedAt)}
                      onEdit={() => openEdit(p)}
                      onArchive={() => onArchive(p)}
                      onRestore={() => onRestore(p)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PersonFormModal
        open={modalOpen}
        personId={editingId}
        onClose={() => setModalOpen(false)}
        onSaved={reload}
      />

      {importOpen && (
        <ImportCollaborateursModal onClose={() => setImportOpen(false)} onImported={reload} />
      )}
    </div>
  );
}
